import React, { useEffect, useState } from 'react';
import { Button, Form, Input, Label, Spinner } from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import AuthCardLayout from './AuthCardLayout';
import LoginFieldError from './LoginFieldError';
import AuthenticatorSetup from './Mfa/AuthenticatorSetup';
import CodeField from './Mfa/CodeField';
import RecoveryCodesPanel from './Mfa/RecoveryCodesPanel';
import {
  AUTH_ERROR_CODES,
  checkSupplierRegistrationEmail,
  completeSupplierRegistration,
  getAuthErrorCode,
  getAuthErrorMessage,
  startSupplierRegistration,
  verifySupplierRegistrationEmail,
} from './Mfa/supplierAuthApi';

const RESEND_COOLDOWN_SECONDS = 60;
const REGISTRATION_TEAM_EMAIL = 'hvergistration@pertamina.com';

// Sama dengan aturan password Identity di BE (Program.cs AddIdentity).
const PASSWORD_RULES = [
  { test: v => v.length >= 8, label: 'At least 8 characters' },
  { test: v => /[A-Z]/.test(v), label: 'One uppercase letter' },
  { test: v => /[a-z]/.test(v), label: 'One lowercase letter' },
  { test: v => /\d/.test(v), label: 'One number' },
  { test: v => /[^A-Za-z0-9]/.test(v), label: 'One special character' },
];

// Kasus yang bukan "error" melainkan informasi + arahan ke user.
// NOT_IN_DMUT punya tampilan sendiri (hasil cek DMUT), jadi tidak ada di sini.
const NOTICES = {
  [AUTH_ERROR_CODES.alreadyRegistered]: {
    icon: 'ri-user-follow-line',
    title: 'Account already registered',
    body: 'This email already has a HYROC account. Please sign in with your email and password.',
    showSignIn: true,
  },
  [AUTH_ERROR_CODES.pertaminaAccount]: {
    icon: 'ri-building-line',
    title: 'Pertamina account',
    body: 'Pertamina employees do not need to register. Please sign in with idAMan.',
    showSignIn: true,
  },
  [AUTH_ERROR_CODES.internalAccount]: {
    icon: 'ri-building-line',
    title: 'Internal account',
    body: 'This email belongs to an internal account and cannot be registered as a Registered Supplier. Please contact the administrator.',
    showSignIn: true,
  },
};

const HEADERS = {
  email: {
    title: 'Register as Registered Supplier',
    subtitle: 'Use your trading or settlement email registered in DMUT.',
  },
  verify: {
    title: 'Verify your email',
    subtitle: 'We sent a 6-digit verification code to your email.',
  },
  setup: {
    title: 'Secure your account',
    subtitle: 'Create a password and set up two-factor authentication.',
  },
  done: {
    title: 'Registration complete',
    subtitle: 'Save your recovery codes before signing in.',
  },
};

const PrimaryButton = ({
  busy,
  busyLabel,
  icon,
  className = '',
  children,
  ...rest
}) => (
  <Button
    type='submit'
    disabled={busy}
    className={`btn login-btn-primary w-100 d-flex align-items-center justify-content-center ${className}`}
    {...rest}
  >
    {busy ? (
      <>
        <Spinner size='sm' className='me-2' aria-hidden='true' />
        {busyLabel}
      </>
    ) : (
      <>
        <i className={`${icon} me-2`} aria-hidden='true' />
        {children}
      </>
    )}
  </Button>
);

const SupplierRegister = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  // Hasil cek DMUT untuk dmutCheck.email: { found: true, companyName, status } | { found: false }.
  const [dmutCheck, setDmutCheck] = useState(null);
  const [emailCode, setEmailCode] = useState('');
  const [resendIn, setResendIn] = useState(0);
  const [registration, setRegistration] = useState(null);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [passwordShow, setPasswordShow] = useState(false);

  document.title = 'Register | HYROC';

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const timer = setTimeout(() => setResendIn(s => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  const restart = () => {
    setStep('email');
    setNotice(null);
    setError('');
    setEmailCode('');
    setRegistration(null);
  };

  // Kode NOT_IN_DMUT dan NOTICES ditampilkan sebagai informasi di langkah email, bukan error.
  const showRejection = (err, targetEmail) => {
    const code = getAuthErrorCode(err);
    if (code === AUTH_ERROR_CODES.notInDmut) {
      setDmutCheck({ email: targetEmail, found: false });
      setStep('email');
    } else if (NOTICES[code]) {
      setNotice(NOTICES[code]);
      setStep('email');
    } else {
      setError(getAuthErrorMessage(err));
    }
  };

  const checkEmail = async targetEmail => {
    setChecking(true);
    setError('');
    setNotice(null);
    setDmutCheck(null);
    try {
      const data = await checkSupplierRegistrationEmail(targetEmail);
      setDmutCheck({
        email: targetEmail,
        found: true,
        companyName: data?.companyName,
        status: data?.status,
      });
    } catch (err) {
      showRejection(err, targetEmail);
    } finally {
      setChecking(false);
    }
  };

  const sendCode = async targetEmail => {
    setBusy(true);
    setError('');
    setNotice(null);
    try {
      await startSupplierRegistration(targetEmail);
      setEmail(targetEmail);
      setEmailCode('');
      setResendIn(RESEND_COOLDOWN_SECONDS);
      setStep('verify');
    } catch (err) {
      showRejection(err, targetEmail);
    } finally {
      setBusy(false);
    }
  };

  const normalizedEmail = value => value.trim().toLowerCase();

  const emailForm = useFormik({
    initialValues: { email: '' },
    validationSchema: Yup.object({
      email: Yup.string()
        .trim()
        .email('Please enter a valid email address.')
        .required('Please enter your DMUT email.'),
    }),
    // Enter di field email: cek DMUT dulu; kode baru dikirim setelah email terverifikasi.
    onSubmit: values => {
      const targetEmail = normalizedEmail(values.email);
      if (dmutCheck?.found && dmutCheck.email === targetEmail) {
        sendCode(targetEmail);
      } else {
        checkEmail(targetEmail);
      }
    },
  });

  const currentDmutCheck =
    dmutCheck && dmutCheck.email === normalizedEmail(emailForm.values.email)
      ? dmutCheck
      : null;
  const dmutVerified = !!currentDmutCheck?.found;
  const dmutNotFound = currentDmutCheck?.found === false;

  const submitEmailCode = async e => {
    e.preventDefault();
    if (busy) return;
    if (emailCode.length !== 6) {
      setError('Please enter the 6-digit code from the email.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const data = await verifySupplierRegistrationEmail(email, emailCode);
      setRegistration(data);
      setStep('setup');
    } catch (err) {
      setError(getAuthErrorMessage(err));
      if (getAuthErrorCode(err) === AUTH_ERROR_CODES.invalidCode) {
        setEmailCode('');
      }
    } finally {
      setBusy(false);
    }
  };

  const setupForm = useFormik({
    initialValues: { password: '', confirmPassword: '', code: '' },
    validationSchema: Yup.object({
      password: Yup.string()
        .required('Please create a password.')
        .test(
          'rules',
          'Password does not meet all the requirements.',
          v => !!v && PASSWORD_RULES.every(r => r.test(v))
        ),
      confirmPassword: Yup.string()
        .required('Please confirm your password.')
        .oneOf([Yup.ref('password')], 'Passwords do not match.'),
      code: Yup.string()
        .required('Please enter the 6-digit code from your authenticator app.')
        .matches(/^\d{6}$/, 'The code must be 6 digits.'),
    }),
    onSubmit: async values => {
      setBusy(true);
      setError('');
      try {
        const data = await completeSupplierRegistration({
          ticket: registration.ticket,
          password: values.password,
          confirmPassword: values.confirmPassword,
          code: values.code,
        });
        setRecoveryCodes(data?.recoveryCodes || []);
        setStep('done');
      } catch (err) {
        const code = getAuthErrorCode(err);
        if (code === AUTH_ERROR_CODES.ticketInvalid) {
          restart();
        }
        setError(getAuthErrorMessage(err));
        setupForm.setFieldValue('code', '');
      } finally {
        setBusy(false);
      }
    },
  });

  const fieldError = (form, name) =>
    form.touched[name] && form.errors[name] ? form.errors[name] : null;

  const renderError = () =>
    error ? (
      <div className='login-error-banner' role='alert' aria-live='assertive'>
        <div className='login-error-banner__icon'>
          <i className='ri-error-warning-line' aria-hidden='true' />
        </div>
        <div className='login-error-banner__content'>
          <p className='login-error-banner__message mb-0'>{error}</p>
        </div>
        <button
          type='button'
          className='login-error-banner__close'
          onClick={() => setError('')}
          aria-label='Dismiss error'
        >
          <i className='ri-close-line' aria-hidden='true' />
        </button>
      </div>
    ) : null;

  const renderEmailStep = () => (
    <Form
      className='flex-grow-1 d-flex flex-column'
      onSubmit={e => {
        e.preventDefault();
        emailForm.handleSubmit();
      }}
    >
      {notice && (
        <div className='register-notice mb-4' role='status'>
          <i className={notice.icon} aria-hidden='true' />
          <div>
            <p className='register-notice__title mb-1'>{notice.title}</p>
            <p className='register-notice__body mb-0'>{notice.body}</p>
            {notice.showSignIn && (
              <Link to='/login' className='register-notice__link'>
                Go to Sign In
              </Link>
            )}
          </div>
        </div>
      )}

      {dmutNotFound && (
        <div className='register-notice mb-4' role='status'>
          <i className='ri-information-line' aria-hidden='true' />
          <div>
            <p className='register-notice__title mb-1'>
              Email not registered in DMUT
            </p>
            <p className='register-notice__body mb-0'>
              Only trading or settlement emails registered in the DMUT database
              can register as a Registered Supplier. To add or update your
              registered email, please contact the Registration Team at{' '}
              <a
                href={`mailto:${REGISTRATION_TEAM_EMAIL}`}
                className='register-notice__email'
              >
                {REGISTRATION_TEAM_EMAIL}
              </a>
              , then proceed with registration once your data has been updated.
            </p>
          </div>
        </div>
      )}

      {dmutVerified && (
        <div
          className='register-notice register-notice--success mb-4'
          role='status'
        >
          <i className='ri-checkbox-circle-line' aria-hidden='true' />
          <div className='flex-grow-1'>
            <div className='d-flex justify-content-between align-items-start gap-2 mb-1'>
              <p className='register-notice__title mb-0'>
                Email registered in DMUT
              </p>
              <span className='register-notice__tag'>
                {currentDmutCheck.status
                  ? `Verified · ${currentDmutCheck.status}`
                  : 'Verified'}
              </span>
            </div>
            <p className='register-notice__body mb-0'>
              Your email is verified and registered in the DMUT database. You
              may now proceed to send the verification code.
            </p>
            <div className='register-dmut-company'>
              <i className='ri-building-2-line' aria-hidden='true' />
              <div>
                <div className='register-dmut-company__label'>
                  Registered Company
                </div>
                <div className='register-dmut-company__name'>
                  {currentDmutCheck.companyName || '-'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className='mb-4'>
        <div className='d-flex justify-content-between align-items-baseline gap-2'>
          <Label htmlFor='register-email' className='login-label'>
            DMUT Email
          </Label>
          {dmutVerified && (
            <span className='register-dmut-tag is-verified'>
              <i className='ri-check-line' aria-hidden='true' /> Verified in
              DMUT
            </span>
          )}
          {dmutNotFound && (
            <span className='register-dmut-tag is-not-found'>
              Not found in DMUT
            </span>
          )}
        </div>
        <div className='register-check-row'>
          <div
            className={`input-group login-input-group${
              dmutVerified ? ' is-verified' : ''
            }`}
          >
            <span className='input-group-text'>
              <i className='ri-mail-line' aria-hidden='true' />
            </span>
            <Input
              id='register-email'
              name='email'
              type='email'
              className='form-control'
              placeholder='name@company.com'
              autoComplete='email'
              value={emailForm.values.email}
              onChange={e => {
                emailForm.handleChange(e);
                setNotice(null);
              }}
              onBlur={emailForm.handleBlur}
              // Tombol submit terkunci sebelum email terverifikasi, dan browser tidak
              // men-submit form lewat Enter bila tombol submit-nya disabled.
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (!checking && !busy) emailForm.handleSubmit();
                }
              }}
              aria-required='true'
              aria-invalid={fieldError(emailForm, 'email') ? 'true' : 'false'}
              aria-describedby={
                fieldError(emailForm, 'email')
                  ? 'register-email-error'
                  : undefined
              }
              invalid={!!fieldError(emailForm, 'email')}
            />
            {dmutVerified && (
              <span className='input-group-text register-check-row__ok'>
                <i className='ri-checkbox-circle-line' aria-hidden='true' />
              </span>
            )}
          </div>
          <Button
            type='button'
            className={`btn register-check-btn${
              dmutVerified ? ' is-recheck' : ''
            }`}
            disabled={checking || busy}
            onClick={() => {
              emailForm.setTouched({ email: true });
              emailForm.validateForm().then(errors => {
                if (!errors.email) {
                  checkEmail(normalizedEmail(emailForm.values.email));
                }
              });
            }}
          >
            {checking ? (
              <Spinner size='sm' className='me-2' aria-hidden='true' />
            ) : (
              <i
                className={`${
                  dmutVerified ? 'ri-refresh-line' : 'ri-search-line'
                } me-2`}
                aria-hidden='true'
              />
            )}
            {dmutVerified ? 'Re-check' : 'Check Email'}
          </Button>
        </div>
        <LoginFieldError
          id='register-email-error'
          message={fieldError(emailForm, 'email')}
        />
      </div>

      <PrimaryButton
        busy={busy}
        busyLabel='Sending...'
        icon={dmutVerified ? 'ri-mail-send-line' : 'ri-lock-line'}
        className={dmutVerified ? '' : 'is-locked'}
        disabled={busy || checking || !dmutVerified}
        aria-describedby={dmutVerified ? undefined : 'register-send-locked'}
      >
        Send Verification Code
      </PrimaryButton>
      {!dmutVerified && (
        <p id='register-send-locked' className='register-locked-hint mb-0'>
          {dmutNotFound
            ? 'Button locked because this email is not registered in DMUT.'
            : 'Check your email in DMUT before sending the code.'}
        </p>
      )}
    </Form>
  );

  const renderVerifyStep = () => (
    <Form className='flex-grow-1 d-flex flex-column' onSubmit={submitEmailCode}>
      <p className='register-sent-to mb-3'>
        Code sent to <strong>{email}</strong>
      </p>
      <CodeField
        id='register-email-code'
        label='Verification code'
        icon='ri-mail-check-line'
        value={emailCode}
        onChange={setEmailCode}
        autoFocus
      />
      <PrimaryButton
        busy={busy}
        busyLabel='Verifying...'
        icon='ri-check-double-line'
      >
        Verify Email
      </PrimaryButton>
      <div className='d-flex justify-content-between flex-wrap gap-2 mt-3'>
        <button
          type='button'
          className='btn btn-link p-0 mfa-link'
          onClick={restart}
          disabled={busy}
        >
          <i className='ri-arrow-left-line me-2' aria-hidden='true' />
          Use another email
        </button>
        <button
          type='button'
          className='btn btn-link p-0 mfa-link'
          onClick={() => sendCode(email)}
          disabled={busy || resendIn > 0}
        >
          {resendIn > 0 ? `Resend code in ${resendIn}s` : 'Resend code'}
        </button>
      </div>
    </Form>
  );

  const renderSetupStep = () => {
    const password = setupForm.values.password;
    return (
      <Form
        className='flex-grow-1 d-flex flex-column'
        onSubmit={e => {
          e.preventDefault();
          setupForm.handleSubmit();
        }}
      >
        <div className='register-account mb-3'>
          <i className='ri-building-2-line' aria-hidden='true' />
          <div>
            <div className='register-account__company'>
              {registration.companyName || 'Registered Supplier'}
            </div>
            <div className='register-account__email'>{registration.email}</div>
          </div>
        </div>

        <div className='mb-3'>
          <Label htmlFor='register-password' className='login-label'>
            Password
          </Label>
          <div className='position-relative auth-pass-inputgroup'>
            <div className='input-group login-input-group is-prominent'>
              <span className='input-group-text'>
                <i className='ri-lock-2-line' aria-hidden='true' />
              </span>
              <Input
                id='register-password'
                name='password'
                type={passwordShow ? 'text' : 'password'}
                className='form-control pe-5'
                placeholder='Create a password'
                autoComplete='new-password'
                value={password}
                onChange={setupForm.handleChange}
                onBlur={setupForm.handleBlur}
                aria-required='true'
                aria-describedby='register-password-rules'
                invalid={!!fieldError(setupForm, 'password')}
              />
            </div>
            <button
              className='btn btn-link position-absolute end-0 top-50 translate-middle-y text-decoration-none material-shadow-none login-password-toggle'
              type='button'
              onClick={() => setPasswordShow(s => !s)}
              aria-label='Show password'
              aria-pressed={passwordShow}
              aria-controls='register-password'
            >
              <i
                className={`ri-${passwordShow ? 'eye-off' : 'eye'}-line align-middle`}
                aria-hidden='true'
              />
            </button>
          </div>
          <ul id='register-password-rules' className='register-password-rules'>
            {PASSWORD_RULES.map(rule => {
              const ok = rule.test(password);
              return (
                <li key={rule.label} className={ok ? 'is-valid' : ''}>
                  <i
                    className={
                      ok ? 'ri-checkbox-circle-line' : 'ri-circle-line'
                    }
                    aria-hidden='true'
                  />
                  {rule.label}
                </li>
              );
            })}
          </ul>
        </div>

        <div className='mb-4'>
          <Label htmlFor='register-confirm-password' className='login-label'>
            Confirm Password
          </Label>
          <div className='input-group login-input-group is-prominent'>
            <span className='input-group-text'>
              <i className='ri-lock-password-line' aria-hidden='true' />
            </span>
            <Input
              id='register-confirm-password'
              name='confirmPassword'
              type={passwordShow ? 'text' : 'password'}
              className='form-control'
              placeholder='Re-enter your password'
              autoComplete='new-password'
              value={setupForm.values.confirmPassword}
              onChange={setupForm.handleChange}
              onBlur={setupForm.handleBlur}
              aria-required='true'
              invalid={!!fieldError(setupForm, 'confirmPassword')}
            />
          </div>
          <LoginFieldError
            id='register-confirm-password-error'
            message={fieldError(setupForm, 'confirmPassword')}
          />
        </div>

        <p className='login-label mb-2'>Two-factor authentication</p>
        <AuthenticatorSetup
          sharedKey={registration.sharedKey}
          authenticatorUri={registration.authenticatorUri}
        />
        <CodeField
          id='register-totp-code'
          label='Authentication code'
          value={setupForm.values.code}
          onChange={v => setupForm.setFieldValue('code', v)}
          error={fieldError(setupForm, 'code')}
        />

        <PrimaryButton
          busy={busy}
          busyLabel='Registering...'
          icon='ri-user-add-line'
        >
          Complete Registration
        </PrimaryButton>
      </Form>
    );
  };

  const header = HEADERS[step];

  return (
    <AuthCardLayout title={header.title} subtitle={header.subtitle}>
      {renderError()}

      {step === 'email' && renderEmailStep()}
      {step === 'verify' && renderVerifyStep()}
      {step === 'setup' && registration && renderSetupStep()}
      {step === 'done' && (
        <RecoveryCodesPanel
          codes={recoveryCodes}
          email={registration?.email}
          continueLabel='Go to Sign In'
          onContinue={() => navigate('/login', { replace: true })}
        />
      )}

      {step !== 'done' && (
        <p className='login-register-note text-center mt-4 mb-0'>
          Already have an account? <Link to='/login'>Sign in</Link>
        </p>
      )}
    </AuthCardLayout>
  );
};

export default SupplierRegister;
